import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InspecaoAtrasada } from '@/hooks/useGestorPerformance';

interface DelayedInspectionsChartProps {
  data: InspecaoAtrasada[];
}

const DelayedInspectionsChart: React.FC<DelayedInspectionsChartProps> = ({ data }) => {
  // Agrupar por hub
  const atrasosPorHub = data.reduce((acc, item) => {
    if (!acc[item.hub_name]) {
      acc[item.hub_name] = { count: 0, totalDias: 0 };
    }
    acc[item.hub_name].count++;
    acc[item.hub_name].totalDias += item.dias_atraso;
    return acc;
  }, {} as Record<string, { count: number; totalDias: number }>);

  const chartData = Object.entries(atrasosPorHub)
    .map(([hub, values]) => ({
      name: hub.replace('Hub ', ''),
      quantidade: values.count,
      mediaDias: Math.round(values.totalDias / values.count)
    }))
    .sort((a, b) => b.quantidade - a.quantidade);

  // Todas as barras em vermelho para destacar atrasos
  const getBarColor = () => 'hsl(var(--destructive))';

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">Inspeções Atrasadas por Hub</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-success text-sm">
            ✓ Nenhuma inspeção atrasada
          </div>
        ) : (
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="hsl(var(--border))" />
                <XAxis 
                  type="number" 
                  tick={{ fontSize: 11 }} 
                  tickLine={false} 
                  axisLine={false}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={100}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                contentStyle={{
                    backgroundColor: 'hsl(var(--chart-tooltip-bg))',
                    border: '1px solid hsl(var(--chart-tooltip-border))',
                    borderRadius: 'var(--chart-tooltip-radius)',
                    fontSize: '12px',
                    boxShadow: 'var(--chart-tooltip-shadow)',
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'quantidade') return [value, 'Inspeções atrasadas'];
                    if (name === 'mediaDias') return [`${value} dias`, 'Atraso médio'];
                    return [value, name];
                  }}
                />
                <Bar dataKey="quantidade" radius={[0, 4, 4, 0]} maxBarSize={28}>
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor()} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Total: {data.length} inspeções pendentes
        </p>
      </CardContent>
    </Card>
  );
};

export default DelayedInspectionsChart;
